import { useEffect, useSyncExternalStore } from 'react';
import { create } from 'zustand';
import type { MenuItem, MenuItemVariant, AddonOption } from '@/features/menu/types';
import type { CartItem, OrderType } from './types/cart';

const CART_STORAGE_KEY = 'publicOrderCart';

const storageKeys = (restaurantId: string) => ({
  cart: `${CART_STORAGE_KEY}_${restaurantId}`,
  notes: `${CART_STORAGE_KEY}_${restaurantId}_orderNotes`,
});

function loadCart(restaurantId: string): CartItem[] {
  try {
    const raw = localStorage.getItem(storageKeys(restaurantId).cart);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCart(restaurantId: string, cart: CartItem[]) {
  try {
    localStorage.setItem(storageKeys(restaurantId).cart, JSON.stringify(cart));
  } catch {
    /* ignore quota / serialization errors */
  }
}

function loadNotes(restaurantId: string): string {
  try {
    return localStorage.getItem(storageKeys(restaurantId).notes) ?? '';
  } catch {
    return '';
  }
}

function saveNotes(restaurantId: string, notes: string) {
  try {
    localStorage.setItem(storageKeys(restaurantId).notes, notes);
  } catch {
    /* ignore */
  }
}

function getLineKey(c: CartItem): string {
  if (c.comboDiscountId) return `combo-${c.comboDiscountId}`;
  return `${c.item.id}-${c.selectedVariant?.id || 'default'}-${JSON.stringify(c.selectedAddons ?? {})}-${c.note ?? ''}`;
}

function computeTotal(cart: CartItem[]): number {
  return cart.reduce((acc, c) => {
    if (c.comboDiscountId && c.comboPrice != null) {
      return acc + c.comboPrice * c.quantity;
    }
    let price = 0;
    if (c.selectedVariant) {
      price = c.selectedVariant.price;
    } else if (c.item.hasVariants && c.item.variants?.length) {
      price = Math.min(...c.item.variants.map((v) => v.price));
    } else {
      price = c.item.basePrice ?? c.item.price ?? 0;
    }
    if (c.selectedAddons) {
      Object.values(c.selectedAddons)
        .flat()
        .forEach((addon) => {
          if (c.selectedVariant && addon.hasVariants && addon.variants?.length) {
            const v = addon.variants.find((x) => x.id === c.selectedVariant!.id);
            price += v != null ? v.price : addon.basePrice ?? 0;
          } else {
            price += addon.basePrice ?? 0;
          }
        });
    }
    return acc + price * c.quantity;
  }, 0);
}

export interface AppliedDeal {
  discountId: string;
  name: string;
  type: 'COMBO' | 'DEAL_SECTION' | 'ENTIRE_ORDER';
}

interface PublicOrderCartState {
  restaurantId: string | null;
  cart: CartItem[];
  orderType: OrderType;
  orderNotes: string;
  appliedDeal: AppliedDeal | null;
  /** Bare local datetime "yyyy-MM-ddTHH:mm:ss" — null means order ASAP. */
  scheduledFor: string | null;

  bindRestaurant: (restaurantId: string) => void;
  setOrderType: (type: OrderType) => void;
  setOrderNotes: (notes: string) => void;
  setAppliedDeal: (deal: AppliedDeal | null) => void;
  setScheduledFor: (value: string | null) => void;
  addToCart: (
    item: MenuItem,
    selectedVariant?: MenuItemVariant,
    selectedAddons?: Record<string, AddonOption[]>,
    quantity?: number,
    note?: string,
  ) => void;
  addComboToCart: (params: {
    discountId: string;
    name: string;
    comboPrice: number;
    comboItems: Array<{ item: MenuItem; quantity: number }>;
  }) => void;
  removeFromCart: (lineKey: string) => void;
  updateCartQuantity: (lineKey: string, quantity: number) => void;
  clearCart: () => void;
}

export const usePublicOrderCartStore = create<PublicOrderCartState>((set, get) => ({
  restaurantId: null,
  cart: [],
  orderType: 'delivery',
  orderNotes: '',
  appliedDeal: null,
  scheduledFor: null,

  bindRestaurant: (restaurantId) => {
    if (get().restaurantId === restaurantId) return;
    const cart = loadCart(restaurantId);
    const orderNotes = loadNotes(restaurantId);
    set({ restaurantId, cart, orderNotes });

    try {
      const raw = sessionStorage.getItem('pendingCartItem');
      if (raw) {
        const { item, quantity, selectedVariant, selectedAddons, note } = JSON.parse(raw);
        if (item?.id) {
          get().addToCart(item, selectedVariant, selectedAddons ?? undefined, quantity ?? 1, note);
        }
        sessionStorage.removeItem('pendingCartItem');
      }
    } catch {
      sessionStorage.removeItem('pendingCartItem');
    }
  },

  setOrderType: (orderType) => set({ orderType }),

  setAppliedDeal: (appliedDeal) => set({ appliedDeal }),
  setScheduledFor: (scheduledFor) => set({ scheduledFor }),

  setOrderNotes: (orderNotes) => {
    const { restaurantId } = get();
    if (restaurantId) saveNotes(restaurantId, orderNotes);
    set({ orderNotes });
  },

  addToCart: (item, selectedVariant, selectedAddons, quantity = 1, note) => {
    const noteTrimmed = note?.trim() || undefined;
    const { cart, restaurantId } = get();
    const existingIndex = cart.findIndex((c) => {
      if (c.item.id !== item.id) return false;
      const variantMatch = c.selectedVariant?.id === selectedVariant?.id;
      const addonsMatch = JSON.stringify(c.selectedAddons) === JSON.stringify(selectedAddons);
      const noteMatch = (c.note ?? '') === (noteTrimmed ?? '');
      return variantMatch && addonsMatch && noteMatch;
    });
    const next =
      existingIndex >= 0
        ? cart.map((c, idx) => (idx === existingIndex ? { ...c, quantity: c.quantity + quantity } : c))
        : [...cart, { item, quantity, selectedVariant, selectedAddons, note: noteTrimmed }];
    if (restaurantId) saveCart(restaurantId, next);
    set({ cart: next });
  },

  addComboToCart: ({ discountId, name, comboPrice, comboItems }) => {
    const { cart, restaurantId } = get();
    const comboItem: MenuItem = {
      id: `combo-${discountId}`,
      name,
      basePrice: comboPrice,
      price: comboPrice,
      isActive: true,
    } as unknown as MenuItem;
    const existing = cart.findIndex((c) => c.comboDiscountId === discountId);
    const next =
      existing >= 0
        ? cart.map((c, i) => (i === existing ? { ...c, quantity: c.quantity + 1 } : c))
        : [...cart, { item: comboItem, quantity: 1, comboDiscountId: discountId, comboPrice, comboItems }];
    if (restaurantId) saveCart(restaurantId, next);
    set({ cart: next, appliedDeal: { discountId, name, type: 'COMBO' } });
  },

  removeFromCart: (lineKey) => {
    const { cart, restaurantId } = get();
    const next = cart.filter((c) => getLineKey(c) !== lineKey);
    if (restaurantId) saveCart(restaurantId, next);
    set({ cart: next });
  },

  updateCartQuantity: (lineKey, quantity) => {
    const { cart, restaurantId } = get();
    const next = cart.map((c) => (getLineKey(c) === lineKey ? { ...c, quantity } : c));
    if (restaurantId) saveCart(restaurantId, next);
    set({ cart: next });
  },

  clearCart: () => {
    const { restaurantId } = get();
    if (restaurantId) saveCart(restaurantId, []);
    set({ cart: [], appliedDeal: null, scheduledFor: null });
  },
}));

/**
 * Primary hook for cart-aware components. Requires a restaurantId to have been
 * bound first (see `useBindCartRestaurant` in PublicOrderLayout).
 */
export function usePublicOrderCart() {
  const cart = usePublicOrderCartStore((s) => s.cart);
  const orderType = usePublicOrderCartStore((s) => s.orderType);
  const orderNotes = usePublicOrderCartStore((s) => s.orderNotes);
  const appliedDeal = usePublicOrderCartStore((s) => s.appliedDeal);
  const scheduledFor = usePublicOrderCartStore((s) => s.scheduledFor);
  const setOrderType = usePublicOrderCartStore((s) => s.setOrderType);
  const setOrderNotes = usePublicOrderCartStore((s) => s.setOrderNotes);
  const setAppliedDeal = usePublicOrderCartStore((s) => s.setAppliedDeal);
  const setScheduledFor = usePublicOrderCartStore((s) => s.setScheduledFor);
  const addToCart = usePublicOrderCartStore((s) => s.addToCart);
  const addComboToCart = usePublicOrderCartStore((s) => s.addComboToCart);
  const removeFromCart = usePublicOrderCartStore((s) => s.removeFromCart);
  const updateCartQuantity = usePublicOrderCartStore((s) => s.updateCartQuantity);
  const clearCart = usePublicOrderCartStore((s) => s.clearCart);

  const cartTotal = computeTotal(cart);
  const cartCount = cart.reduce((acc, c) => acc + c.quantity, 0);

  return {
    cart,
    orderType,
    setOrderType,
    orderNotes,
    setOrderNotes,
    appliedDeal,
    setAppliedDeal,
    scheduledFor,
    setScheduledFor,
    addToCart,
    addComboToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    getLineKey,
    cartTotal,
    cartCount,
  };
}

/** Binds the store to the current restaurantId. Call from the order layout. */
export function useBindCartRestaurant(restaurantId: string | undefined) {
  const bindRestaurant = usePublicOrderCartStore((s) => s.bindRestaurant);
  useEffect(() => {
    if (restaurantId) bindRestaurant(restaurantId);
  }, [restaurantId, bindRestaurant]);
}

export { getLineKey };

/** Read cart count from localStorage — for pages outside the order layout (e.g. landing). */
export function getCartCountFromStorage(restaurantId: string | undefined): number {
  if (!restaurantId) return 0;
  return loadCart(restaurantId).reduce((sum, item) => sum + (item.quantity ?? 1), 0);
}

/** Reactive version of {@link getCartCountFromStorage} that re-reads on storage events. */
export function useCartCountFromStorage(restaurantId: string | undefined): number {
  return useSyncExternalStore(
    (notify) => {
      const handler = (e: StorageEvent) => {
        if (!restaurantId) return;
        if (e.key === storageKeys(restaurantId).cart) notify();
      };
      window.addEventListener('storage', handler);
      return () => window.removeEventListener('storage', handler);
    },
    () => getCartCountFromStorage(restaurantId),
    () => 0,
  );
}
